import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { format, eachDayOfInterval, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { getReceiptList } from '../../services';
import { IReceipt, IReceiptFilters } from '../../types';
import { derivePaymentNature, PAYMENT_NATURE_LABELS, PaymentNature } from '../../helpers/receiptNormalizer';
import {
	CIVIL_DATE_PRESETS,
	CIVIL_DATE_PRESET_LABELS,
	CivilDatePreset,
	formatDateRangeLabel,
	formatDateYmd,
	getDateRangeFromPreset,
} from 'features/common/helpers/datePresets';

// ─── Constantes ───────────────────────────────────────────────────────────────

const MAX_FETCH_SIZE = 100;
const MATRIX_MAX_DAYS = 14;

const NATURE_ORDER: PaymentNature[] = [
	'BANK_TRANSFER',
	'CASH_OFFICE',
	'CARD_GATEWAY',
	'AIRLINE_CARD',
	'POINTS',
	'FISCAL_ADJUSTMENT',
	'UNKNOWN',
];

const NATURE_COLOR: Record<PaymentNature, string> = {
	BANK_TRANSFER: '#1565C0',
	CARD_GATEWAY: '#6A1B9A',
	AIRLINE_CARD: '#E64A19',
	CASH_OFFICE: '#00695C',
	POINTS: '#2E7D32',
	FISCAL_ADJUSTMENT: '#B71C1C',
	UNKNOWN: '#546E7A',
};

const NATURE_BG: Record<PaymentNature, string> = {
	BANK_TRANSFER: '#E3F2FD',
	CARD_GATEWAY: '#F3E5F5',
	AIRLINE_CARD: '#FBE9E7',
	CASH_OFFICE: '#E0F2F1',
	POINTS: '#E8F5E9',
	FISCAL_ADJUSTMENT: '#FFEBEE',
	UNKNOWN: '#ECEFF1',
};

// ─── Tipos internos ───────────────────────────────────────────────────────────

interface ChannelRow {
	nature: PaymentNature;
	entity: string;
	totalUSD: number;
	count: number;
	byDate: Record<string, number>;
}

interface NatureGroup {
	nature: PaymentNature;
	totalUSD: number;
	count: number;
	byDate: Record<string, number>;
	channels: ChannelRow[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtUSD = (n: number): string =>
	n === 0 ? '—' : new Intl.NumberFormat('es-UY', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);

const fmtUSDFull = (n: number): string =>
	new Intl.NumberFormat('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

function resolveEntityName(receipt: IReceipt, nature: PaymentNature): string {
	switch (nature) {
		case 'BANK_TRANSFER':
			return receipt.platformType === 'WireTransfer'
				? receipt.extBankName ?? receipt.extBranchName ?? 'Wire Transfer'
				: receipt.extBranchName ?? receipt.extBankName ?? 'Banco';
		case 'CASH_OFFICE':
			return receipt.extBranchName ?? 'Caja';
		case 'CARD_GATEWAY':
			return ['Bancard', 'Wompi', 'SiemprePago', 'ATCRedEnlace'].includes(receipt.platformType)
				? receipt.platformType
				: receipt.extBranchName ?? receipt.platformType ?? 'Gateway';
		case 'AIRLINE_CARD':
			return receipt.extAirlineName ?? receipt.platformType ?? 'Aerolínea';
		case 'POINTS':
			return 'Itaú Points';
		case 'FISCAL_ADJUSTMENT':
			return receipt.extBranchName ?? receipt.extCashSourceType ?? 'Ajuste';
		default:
			return receipt.extBranchName ?? receipt.platformType ?? 'Desconocido';
	}
}

function buildGroups(items: IReceipt[]): NatureGroup[] {
	const rowMap = new Map<string, ChannelRow>();

	for (const item of items) {
		const nature = derivePaymentNature(item.platformType, item.extCashSourceType, item.extBranchName);
		const entity = resolveEntityName(item, nature);
		const dateKey = item.createdAt.slice(0, 10);
		const key = `${nature}::${entity}`;
		const amount = item.amountInMainCurrency;

		if (!rowMap.has(key)) {
			rowMap.set(key, { nature, entity, totalUSD: 0, count: 0, byDate: {} });
		}
		const row = rowMap.get(key);
		if (row) {
			row.totalUSD += amount;
			row.count += 1;
			row.byDate[dateKey] = (row.byDate[dateKey] ?? 0) + amount;
		}
	}

	const natureMap = new Map<PaymentNature, NatureGroup>();
	for (const row of rowMap.values()) {
		if (!natureMap.has(row.nature)) {
			natureMap.set(row.nature, {
				nature: row.nature,
				totalUSD: 0,
				count: 0,
				byDate: {},
				channels: [],
			});
		}
		const group = natureMap.get(row.nature);
		if (group) {
			group.totalUSD += row.totalUSD;
			group.count += row.count;
			group.channels.push(row);
			for (const [d, v] of Object.entries(row.byDate)) {
				group.byDate[d] = (group.byDate[d] ?? 0) + v;
			}
		}
	}

	return NATURE_ORDER.filter((n) => natureMap.has(n))
		.map((n) => {
			const g = natureMap.get(n);
			if (g) g.channels.sort((a, b) => b.totalUSD - a.totalUSD);
			return g;
		})
		.filter((g): g is NatureGroup => g !== undefined);
}

// ─── Barra de presets de fecha ────────────────────────────────────────────────

interface DateBarProps {
	activePreset: CivilDatePreset | '';
	onPresetToggle: (p: CivilDatePreset) => void;
}

const DateBar = ({ activePreset, onPresetToggle }: DateBarProps): JSX.Element => (
	<Paper
		elevation={0}
		sx={{
			p: 1.5,
			mb: 2,
			borderRadius: 2,
			border: '1px solid rgba(0,0,0,0.08)',
			background: 'linear-gradient(180deg,#fafbff 0%,#ffffff 100%)',
		}}
	>
		<Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
			<Typography variant="caption" color="text.secondary" sx={{ minWidth: 64, fontWeight: 600 }}>
				Período
			</Typography>
			{CIVIL_DATE_PRESETS.map((preset) => {
				const range = getDateRangeFromPreset(preset);
				const isActive = activePreset === preset;
				return (
					<Tooltip key={preset} title={formatDateRangeLabel(range.from, range.to)}>
						<Button
							size="small"
							variant={isActive ? 'contained' : 'outlined'}
							onClick={() => onPresetToggle(preset)}
							sx={{ minWidth: 'auto', px: 1.25, py: 0.35, fontSize: '0.8125rem', textTransform: 'none' }}
						>
							{CIVIL_DATE_PRESET_LABELS[preset]}
						</Button>
					</Tooltip>
				);
			})}
		</Box>
	</Paper>
);

// ─── Componente principal ─────────────────────────────────────────────────────

export const ReceiptsByChannelPanel = (): JSX.Element => {
	const [items, setItems] = useState<IReceipt[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [preset, setPreset] = useState<CivilDatePreset | ''>('lastWeek');
	const [dateFrom, setDateFrom] = useState<Date>(() => getDateRangeFromPreset('lastWeek').from);
	const [dateTo, setDateTo] = useState<Date>(() => getDateRangeFromPreset('lastWeek').to);

	const fetchData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const filters: IReceiptFilters = {
				dateFrom: formatDateYmd(dateFrom),
				dateTo: formatDateYmd(dateTo),
			};
			const res = await getReceiptList(1, MAX_FETCH_SIZE, filters);
			setItems(res.data.elements);
			setTotalCount(res.data.total);
		} catch {
			setError('No se pudieron cargar los recibos. Intentá de nuevo.');
		} finally {
			setLoading(false);
		}
	}, [dateFrom, dateTo]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handlePresetToggle = (p: CivilDatePreset) => {
		if (preset === p) {
			return;
		}
		const { from, to } = getDateRangeFromPreset(p);
		setPreset(p);
		setDateFrom(from);
		setDateTo(to);
	};

	const groups = useMemo(() => buildGroups(items), [items]);

	const dayCount = differenceInDays(dateTo, dateFrom) + 1;
	const isMatrixMode = dayCount <= MATRIX_MAX_DAYS;

	const days = useMemo<Date[]>(() => {
		if (!isMatrixMode) return [];
		return eachDayOfInterval({ start: dateFrom, end: dateTo });
	}, [dateFrom, dateTo, isMatrixMode]);

	const dayKeys = useMemo(() => days.map((d) => formatDateYmd(d)), [days]);

	const grandTotalUSD = groups.reduce((acc, g) => acc + g.totalUSD, 0);
	const grandTotalByDate = useMemo(() => {
		const acc: Record<string, number> = {};
		for (const g of groups) {
			for (const [d, v] of Object.entries(g.byDate)) {
				acc[d] = (acc[d] ?? 0) + v;
			}
		}
		return acc;
	}, [groups]);

	const isPartial = totalCount > items.length;

	// ─── Render ───────────────────────────────────────────────────────────────

	return (
		<Box>
			<Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
				<Typography variant="h6" fontWeight="bold">
					Ingresos por canal
				</Typography>
				<Tooltip title="Recargar datos">
					<span>
						<Button size="small" startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading} variant="outlined">
							Actualizar
						</Button>
					</span>
				</Tooltip>
			</Box>

			<DateBar activePreset={preset} onPresetToggle={handlePresetToggle} />

			{isPartial && !loading && (
				<Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />} sx={{ mb: 2 }}>
					Se muestran {items.length} de {totalCount} recibos. Los totales son parciales. Reducí el período para ver
					todos los datos.
				</Alert>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading && (
				<Box display="flex" justifyContent="center" py={6}>
					<CircularProgress />
				</Box>
			)}

			{!loading && !error && groups.length === 0 && (
				<Box display="flex" justifyContent="center" py={6}>
					<Typography color="text.secondary">No hay recibos en el período seleccionado.</Typography>
				</Box>
			)}

			{!loading && groups.length > 0 && (
				<TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
					<Table size="small" sx={{ minWidth: isMatrixMode ? 700 : 420 }}>
						<TableHead>
							<TableRow sx={{ backgroundColor: 'grey.100' }}>
								<TableCell
									sx={{
										fontWeight: 'bold',
										minWidth: 200,
										position: 'sticky',
										left: 0,
										backgroundColor: 'grey.100',
										zIndex: 1,
									}}
								>
									Canal
								</TableCell>
								{isMatrixMode ? (
									days.map((d) => (
										<TableCell
											key={formatDateYmd(d)}
											align="right"
											sx={{ fontWeight: 'bold', minWidth: 80, whiteSpace: 'nowrap' }}
										>
											{format(d, 'EEE dd', { locale: es })}
										</TableCell>
									))
								) : (
									<TableCell align="right" sx={{ fontWeight: 'bold' }}>
										Transacciones
									</TableCell>
								)}
								<TableCell align="right" sx={{ fontWeight: 'bold', minWidth: 90, whiteSpace: 'nowrap' }}>
									Total USD
								</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{groups.map((group) => (
								<React.Fragment key={group.nature}>
									{/* ── Fila de grupo (naturaleza) ── */}
									<TableRow sx={{ backgroundColor: NATURE_BG[group.nature] }}>
										<TableCell
											sx={{
												fontWeight: 'bold',
												fontSize: '0.8rem',
												color: NATURE_COLOR[group.nature],
												position: 'sticky',
												left: 0,
												backgroundColor: NATURE_BG[group.nature],
												zIndex: 1,
											}}
										>
											<Box display="flex" alignItems="center" gap={0.75}>
												<Box
													sx={{
														width: 8,
														height: 8,
														borderRadius: '50%',
														bgcolor: NATURE_COLOR[group.nature],
														flexShrink: 0,
													}}
												/>
												{PAYMENT_NATURE_LABELS[group.nature].toUpperCase()}
												<Chip
													label={group.count}
													size="small"
													sx={{
														height: 16,
														fontSize: '0.68rem',
														bgcolor: NATURE_COLOR[group.nature],
														color: 'white',
														ml: 0.5,
													}}
												/>
											</Box>
										</TableCell>
										{isMatrixMode ? (
											dayKeys.map((dk) => (
												<TableCell
													key={dk}
													align="right"
													sx={{
														fontWeight: 'bold',
														fontSize: '0.78rem',
														color: NATURE_COLOR[group.nature],
													}}
												>
													{group.byDate[dk] ? fmtUSD(group.byDate[dk]) : '—'}
												</TableCell>
											))
										) : (
											<TableCell
												align="right"
												sx={{ fontWeight: 'bold', fontSize: '0.78rem', color: NATURE_COLOR[group.nature] }}
											>
												{group.count}
											</TableCell>
										)}
										<TableCell
											align="right"
											sx={{
												fontWeight: 'bold',
												fontSize: '0.82rem',
												color: NATURE_COLOR[group.nature],
											}}
										>
											{fmtUSD(group.totalUSD)}
										</TableCell>
									</TableRow>

									{/* ── Filas de canal ── */}
									{group.channels.map((ch) => (
										<TableRow
											key={`${ch.nature}::${ch.entity}`}
											hover
											sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
										>
											<TableCell
												sx={{
													pl: 4,
													fontSize: '0.8rem',
													color: 'text.primary',
													position: 'sticky',
													left: 0,
													backgroundColor: 'background.paper',
												}}
											>
												<Tooltip title={`${ch.count} transacciones`} placement="right">
													<span>{ch.entity}</span>
												</Tooltip>
											</TableCell>
											{isMatrixMode ? (
												dayKeys.map((dk) => (
													<TableCell key={dk} align="right" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
														{ch.byDate[dk] ? fmtUSD(ch.byDate[dk]) : ''}
													</TableCell>
												))
											) : (
												<TableCell align="right" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
													{ch.count}
												</TableCell>
											)}
											<TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
												<Tooltip title={`USD ${fmtUSDFull(ch.totalUSD)}`} placement="left">
													<span>{fmtUSD(ch.totalUSD)}</span>
												</Tooltip>
											</TableCell>
										</TableRow>
									))}
								</React.Fragment>
							))}

							{/* ── Fila de totales ── */}
							<TableRow sx={{ backgroundColor: 'grey.800' }}>
								<TableCell
									sx={{
										fontWeight: 'bold',
										fontSize: '0.85rem',
										color: 'common.white',
										position: 'sticky',
										left: 0,
										backgroundColor: 'grey.800',
										zIndex: 1,
									}}
								>
									TOTAL
								</TableCell>
								{isMatrixMode ? (
									dayKeys.map((dk) => (
										<TableCell
											key={dk}
											align="right"
											sx={{ fontWeight: 'bold', fontSize: '0.82rem', color: 'common.white' }}
										>
											{grandTotalByDate[dk] ? fmtUSD(grandTotalByDate[dk]) : '—'}
										</TableCell>
									))
								) : (
									<TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.82rem', color: 'common.white' }}>
										{items.length}
									</TableCell>
								)}
								<TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'common.white' }}>
									{fmtUSD(grandTotalUSD)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{!loading && groups.length > 0 && (
				<Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
					Montos en USD. Período: {formatDateRangeLabel(dateFrom, dateTo)} · {items.length} transacciones cargadas.
				</Typography>
			)}
		</Box>
	);
};
